import { useEffect, useRef, useState } from "react";
import { api, registerEmail, loginEmail, GOOGLE_LOGIN_URL } from "../api";
import { useUI } from "../lib/theme";

type Tab = "register" | "login";
type Step = "form" | "consent";

const ERR_MAP: Record<string, string> = {
  email_invalido: "auth_err_email_invalido",
  password_corto: "auth_err_password_corto",
  invitacion_requerida: "auth_err_invitacion_requerida",
  codigo_invalido: "auth_err_codigo_invalido",
  codigo_caducado: "auth_err_codigo_caducado",
  codigo_agotado: "auth_err_codigo_agotado",
  email_en_uso: "auth_err_email_en_uso",
  email_solo_google: "auth_err_email_solo_google",
  credenciales_incorrectas: "auth_err_credenciales_incorrectas",
  acceso_pausado: "auth_err_acceso_pausado",
  registro_fallido: "auth_err_registro_fallido",
};

function errKey(code?: string): string {
  return code ? (ERR_MAP[code] ?? "auth_err_generic") : "auth_err_generic";
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Callback cuando el login/registro fue exitoso y no requiere conectar fuentes. */
  onSuccess: () => void;
  /** Callback cuando el registro fue exitoso y el usuario necesita conectar fuentes. */
  onNeedsConnection: () => void;
  /** Tab inicial (register = default). */
  defaultTab?: Tab;
  /** Código de invitación pre-rellenado (desde ?invite= en la URL). */
  prefillCode?: string;
}

export function AuthModal({ open, onClose, onSuccess, onNeedsConnection, defaultTab = "register", prefillCode }: Props) {
  const { t } = useUI();
  const [tab, setTab] = useState<Tab>(defaultTab);
  const [step, setStep] = useState<Step>("form");
  const [pendingCode, setPendingCode] = useState("");
  const [pendingGoogle, setPendingGoogle] = useState(false);

  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [coachName, setCoachName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTab(defaultTab);
      setStep("form");
      setCode(prefillCode ?? ""); setEmail(""); setPassword(""); setPassword2(""); setCoachName(""); setErr(null); setBusy(false); setPendingCode(""); setPendingGoogle(false);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open, defaultTab]);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  if (!open) return null;

  const handleGoogleRegister = async () => {
    if (busy) return;
    setErr(null);
    const cleanCode = code.trim().toUpperCase();
    if (!cleanCode) { setErr(t("auth_err_code_before_google")); return; }
    setBusy(true);
    try {
      const redeem = await api.redeemCode(cleanCode);
      if (!redeem.ok) { setErr(t(errKey(redeem.error))); setBusy(false); return; }
      if (redeem.coachCode && redeem.coachName) {
        setCoachName(redeem.coachName);
        setPendingCode(cleanCode);
        setPendingGoogle(true);
        setStep("consent");
        setBusy(false);
        return;
      }
      // Código redimido: navegar a Google OAuth (la cookie INVITE_COOKIE ya está puesta).
      window.location.href = GOOGLE_LOGIN_URL;
    } catch { setErr(t("auth_err_generic")); setBusy(false); }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setErr(null);
    // Validar que las contraseñas coinciden.
    if (password !== password2) { setErr(t("auth_err_password_mismatch")); return; }
    // 1. Validar y guardar el código de invitación (deja la cookie).
    const cleanCode = code.trim().toUpperCase();
    if (!cleanCode) { setErr(t("auth_err_invitacion_requerida")); return; }
    setBusy(true);
    try {
      const redeem = await api.redeemCode(cleanCode);
      if (!redeem.ok) { setErr(t(errKey(redeem.error))); setBusy(false); return; }
      // Código coach_link → pantalla de consentimiento antes de crear la cuenta.
      if (redeem.coachCode && redeem.coachName) {
        setCoachName(redeem.coachName);
        setPendingCode(cleanCode);
        setStep("consent");
        setBusy(false);
        return;
      }
      // Código de beta → crear cuenta directamente.
      await doRegister(cleanCode);
    } catch { setErr(t("auth_err_generic")); setBusy(false); }
  };

  const doRegister = async (codeOverride?: string) => {
    setBusy(true);
    try {
      const res = await registerEmail(email.trim(), password, (codeOverride ?? pendingCode) || undefined);
      if (!res.ok) { setErr(t(errKey(res.error))); setBusy(false); return; }
      if (res.needsConnection) onNeedsConnection();
      else onSuccess();
    } catch { setErr(t("auth_err_generic")); setBusy(false); }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setErr(null);
    setBusy(true);
    try {
      const res = await loginEmail(email.trim(), password);
      if (!res.ok) { setErr(t(errKey(res.error))); setBusy(false); return; }
      onSuccess();
    } catch { setErr(t("auth_err_generic")); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-sm rounded-2xl border border-line bg-panel p-6 shadow-2xl">

        {step === "consent" ? (
          <>
            <h2 className="text-lg font-bold text-fg">{t("auth_consent_title")}</h2>
            <p className="mt-2 text-sm text-muted">{t("auth_consent_body").replace("{coachName}", coachName)}</p>
            <div className="mt-6 flex gap-2">
              <button
                onClick={() => {
                  if (pendingGoogle) { window.location.href = GOOGLE_LOGIN_URL; }
                  else { void doRegister(pendingCode || undefined); }
                }}
                disabled={busy}
                className="flex-1 rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-white transition hover:brightness-110 disabled:opacity-60"
              >
                {t("auth_consent_accept")}
              </button>
              <button
                onClick={() => { setStep("form"); setCode(""); setErr(null); setPendingGoogle(false); }}
                className="rounded-xl border border-line px-4 py-2 text-sm text-muted transition hover:text-fg"
              >
                {t("auth_consent_cancel")}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Tabs */}
            <div className="mb-5 flex rounded-xl border border-line p-0.5 text-sm font-medium">
              {(["register", "login"] as Tab[]).map((t2) => (
                <button
                  key={t2}
                  onClick={() => { setTab(t2); setErr(null); }}
                  className={`flex-1 rounded-[10px] py-1.5 transition ${tab === t2 ? "bg-accent text-white" : "text-muted hover:text-fg"}`}
                >
                  {t2 === "register" ? t("auth_tab_register") : t("auth_tab_login")}
                </button>
              ))}
            </div>

            <form onSubmit={tab === "register" ? handleRegister : handleLogin} className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted">{t("auth_email_label")}</label>
                <input
                  ref={inputRef}
                  id="auth-email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setErr(null); }}
                  placeholder={t("auth_email_placeholder")}
                  autoComplete="email"
                  className="w-full rounded-xl border border-line bg-ink/40 px-3 py-2 text-sm text-fg outline-none transition focus:border-accent"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-muted">{t("auth_password_label")}</label>
                <input
                  id={tab === "register" ? "auth-password-new" : "auth-password-current"}
                  name={tab === "register" ? "new-password" : "current-password"}
                  type="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setErr(null); }}
                  placeholder={t("auth_password_placeholder")}
                  autoComplete={tab === "register" ? "new-password" : "current-password"}
                  className="w-full rounded-xl border border-line bg-ink/40 px-3 py-2 text-sm text-fg outline-none transition focus:border-accent"
                />
              </div>

              {tab === "register" && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted">{t("auth_password2_label")}</label>
                  <input
                    id="auth-password-confirm"
                    name="confirm-password"
                    type="password"
                    value={password2}
                    onChange={(e) => { setPassword2(e.target.value); setErr(null); }}
                    placeholder={t("auth_password2_placeholder")}
                    autoComplete="new-password"
                    className="w-full rounded-xl border border-line bg-ink/40 px-3 py-2 text-sm text-fg outline-none transition focus:border-accent"
                  />
                </div>
              )}

              {tab === "register" && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted">{t("auth_code_label")}</label>
                  <input
                    value={code}
                    onChange={(e) => { setCode(e.target.value.toUpperCase()); setErr(null); }}
                    placeholder={t("auth_code_placeholder")}
                    autoComplete="off"
                    spellCheck={false}
                    className="w-full rounded-xl border border-line bg-ink/40 px-3 py-2 font-mono text-sm text-fg outline-none transition focus:border-accent"
                  />
                </div>
              )}

              {err && tab !== "register" && <p className="text-xs font-medium text-red-500">{err}</p>}

              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-xl bg-accent py-2.5 text-sm font-semibold text-white transition hover:brightness-110 disabled:opacity-60"
              >
                {busy ? "…" : tab === "register" ? t("auth_register_btn") : t("auth_login_btn")}
              </button>
            </form>

              <div className="mt-3 flex flex-col gap-2">
              {tab === "login" ? (
                <a
                  href={GOOGLE_LOGIN_URL}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-line py-2.5 text-sm font-medium text-fg transition hover:bg-panel"
                >
                  <GoogleIcon />
                  {t("auth_google_btn")}
                </a>
              ) : (
                <button
                  type="button"
                  disabled={busy}
                  onClick={handleGoogleRegister}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-line py-2.5 text-sm font-medium text-fg transition hover:bg-panel disabled:opacity-60"
                >
                  <GoogleIcon />
                  {t("auth_google_btn")}
                </button>
              )}

              {tab === "register" && err && (
                <p className="text-center text-xs font-medium text-red-500">{err}</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="shrink-0" aria-hidden>
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}
